/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { Alert, Stack, Typography } from '@mui/material'
import React from 'react'

import type { ReviewFeedback } from '@/graphql'
import useT from '@/i18n/useT'

type ReviewFeedbackNoticeProps = {
  feedback: ReviewFeedback | null | undefined
  returnedCount: number | null | undefined
}

function ReviewFeedbackNotice({
  feedback,
  returnedCount,
}: ReviewFeedbackNoticeProps) {
  const { t } = useT()
  const count = returnedCount ?? 0
  if (!feedback && count <= 0) return null

  const note =
    feedback?.note?.trim() ||
    t('labeling.returnedFeedback.noNote', 'No feedback provided')

  return (
    <Alert severity={feedback ? 'warning' : 'info'} sx={{ width: '100%' }}>
      <Stack spacing={0.75}>
        <Typography variant="body2">
          {t('labeling.returnedFeedback.count', {
            count,
            defaultValue: `${count} ${count === 1 ? 'item' : 'items'} returned by reviewer`,
          })}
        </Typography>
        {feedback ? (
          <Typography variant="body2">
            {feedback.reviewerEmail
              ? t('labeling.returnedFeedback.noteWithReviewer', {
                  reviewer: feedback.reviewerEmail,
                  note,
                  defaultValue: `${feedback.reviewerEmail}: ${note}`,
                })
              : note}
          </Typography>
        ) : null}
      </Stack>
    </Alert>
  )
}

export default ReviewFeedbackNotice
